import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AlertCircle, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { COMPANY, SERVICES } from "@/content/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", email: "", phone: "", service: "", message: "", privacy: false, website: "" };
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Otro"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Indica tu nombre.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Introduce un correo electrónico válido.";
  if (f.phone.trim() && !/^[0-9+()\s.-]{6,20}$/.test(f.phone.trim())) e.phone = "Teléfono no válido.";
  if (!f.service) e.service = "Selecciona el servicio que te interesa.";
  if (f.message.trim().length < 10) e.message = "Describe tu proyecto (mínimo 10 caracteres).";
  if (!f.privacy) e.privacy = "Debes aceptar la política de privacidad.";
  return e;
};

const Field = ({ id, label, error, optional, children }) => (
  <div>
    <label htmlFor={id} className="flex justify-between font-display text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1E2228]">
      {label}{optional && <span className="font-normal normal-case tracking-normal text-[#3A414B]">Opcional</span>}
    </label>
    {children}
    {error && <p id={`${id}-error`} data-testid={`${id}-error`} role="alert" className="mt-2 text-sm text-[#A1281C]">{error}</p>}
  </div>
);

const inputCls = "mt-2 w-full border-0 border-b border-[#1E2228]/25 bg-transparent px-0 py-3 text-base text-[#1E2228] placeholder:text-[#3A414B]/60 focus:border-[#8A6C3E] focus:outline-none focus:ring-0 focus-visible:bg-[#F6F4EF] transition-colors";

export default function ContactForm() {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const startedAt = useRef(Date.now());

  useEffect(() => { startedAt.current = Date.now(); }, []);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrors(v);
    if (Object.keys(v).length) return;
    if (!API) { setStatus("error"); setServerError("El formulario no está configurado."); return; }
    setStatus("sending");
    setServerError("");
    try {
      const res = await axios.post(`${API}/contact`, {
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() || null, service: f.service, message: f.message.trim(),
        privacy_accepted: f.privacy, website: f.website, fill_ms: Date.now() - startedAt.current,
      });
      if (res.data?.status === "sent") { setStatus("sent"); setF(EMPTY); }
      else { setStatus("error"); setServerError("No hemos podido confirmar el envío."); }
    } catch (err) {
      const d = err.response?.data?.detail;
      setServerError(typeof d === "string" ? d : "No se ha podido enviar el mensaje.");
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-start justify-center">
        <CheckCircle2 className="h-10 w-10 text-[#8A6C3E]" />
        <h3 className="mt-6 font-display text-2xl font-light uppercase text-[#1E2228]">Mensaje enviado</h3>
        <p className="mt-3 max-w-md text-base leading-relaxed text-[#3A414B]">Hemos recibido tu solicitud. Nos pondremos en contacto contigo lo antes posible.</p>
        <button data-testid="contact-send-another" onClick={() => { setStatus("idle"); startedAt.current = Date.now(); }} className="btn-outline-dark mt-8">Enviar otro mensaje</button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-7">
      <div className="grid gap-7 sm:grid-cols-2">
        <Field id="contact-name" label="Nombre" error={errors.name}>
          <input id="contact-name" data-testid="contact-name-input" autoComplete="name" value={f.name} onChange={set("name")} className={inputCls} placeholder="Tu nombre" maxLength={100} />
        </Field>
        <Field id="contact-phone" label="Teléfono" optional error={errors.phone}>
          <input id="contact-phone" data-testid="contact-phone-input" type="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} className={inputCls} placeholder="+34 ..." maxLength={20} />
        </Field>
      </div>
      <Field id="contact-email" label="Correo electrónico" error={errors.email}>
        <input id="contact-email" data-testid="contact-email-input" type="email" autoComplete="email" value={f.email} onChange={set("email")} className={inputCls} placeholder="tu@correo.com" maxLength={150} />
      </Field>
      <Field id="contact-service" label="Servicio de interés" error={errors.service}>
        <select id="contact-service" data-testid="contact-service-select" value={f.service} onChange={set("service")} aria-invalid={!!errors.service} className={`${inputCls} cursor-pointer`}>
          <option value="">Selecciona un servicio</option>
          {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
      <Field id="contact-message" label="Mensaje" error={errors.message}>
        <textarea id="contact-message" data-testid="contact-message-input" rows={5} value={f.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tipo de espacio, qué te gustaría hacer, plazos aproximados..." maxLength={3000} />
      </Field>
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="contact-website">No rellenar</label>
        <input id="contact-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
      </div>
      <div>
        <label className="flex items-start gap-3 text-sm leading-relaxed text-[#3A414B]">
          <input type="checkbox" data-testid="contact-privacy-checkbox" checked={f.privacy} onChange={set("privacy")} className="mt-1 h-4 w-4 shrink-0 accent-[#8A6C3E]" />
          <span>He leído y acepto la <Link to="/privacidad" data-testid="contact-privacy-link" className="underline decoration-[#8A6C3E] underline-offset-4">política de privacidad</Link>. Los datos se usan solo para responder a esta solicitud y no se guardan en ninguna base de datos de la web.</span>
        </label>
        {errors.privacy && <p data-testid="contact-privacy-error" className="mt-2 text-sm text-[#A1281C]">{errors.privacy}</p>}
      </div>
      {status === "error" && (
        <div data-testid="contact-error" role="alert" className="flex gap-3 border border-[#A1281C]/30 bg-[#A1281C]/5 p-4 text-sm text-[#7A1F16]">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{serverError} Puedes llamarnos al <a href={COMPANY.phoneHref} className="font-semibold underline">{COMPANY.phone}</a> o escribir a <a href={COMPANY.emailHref} className="font-semibold underline">{COMPANY.email}</a>.</span>
        </div>
      )}
      <button type="submit" data-testid="contact-submit-button" disabled={status === "sending"} className="btn-dark group w-full disabled:opacity-70 sm:w-auto">
        {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" />Enviando...</> : <>Solicitar presupuesto<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
}
